import type { RegisterPersonalInfo } from "@profile/contexts/RegisterContext";

import {
  RegistrationEvent,
  type TrackRegistrationEventFn,
} from "./analytics";

export interface PasswordSubmitFormData {
  password: string;
  confirmPassword: string;
}

export function getPasswordSubmitValidationError(
  data: PasswordSubmitFormData,
  personalInfo: RegisterPersonalInfo | undefined,
  otpId: string | undefined,
  otpFactorId: string | undefined,
  requiredMessage: string,
): string | null {
  if (!data.password) {
    return "register:password_required";
  }

  if (data.password !== data.confirmPassword) {
    return "register:passwords_do_not_match";
  }

  if (!personalInfo) {
    return requiredMessage;
  }

  const missingFields =
    !personalInfo.lastName.trim() ||
    !personalInfo.loanNumber.trim() ||
    !personalInfo.ssn.trim() ||
    !otpId?.trim() ||
    !otpFactorId?.trim();

  if (missingFields) {
    return requiredMessage;
  }

  if (!/^\d{9}$/.test(personalInfo.ssn.trim())) {
    return requiredMessage;
  }

  return null;
}

function getAccountCreateEvent(
  isAssumptionLoan: boolean,
  outcome: "success" | "failed",
): RegistrationEvent {
  if (outcome === "success") {
    return isAssumptionLoan
      ? RegistrationEvent.ASSUMPTION_CREATE_SUCCESS
      : RegistrationEvent.CREATE_SUCCESS;
  }

  return isAssumptionLoan
    ? RegistrationEvent.ASSUMPTION_CREATE_FAILED
    : RegistrationEvent.CREATE_FAILED;
}

export function trackAccountCreateAnalytics(
  trackRegistrationEvent: TrackRegistrationEventFn,
  isAssumptionLoan: boolean,
  outcome: "success" | "failed",
  attributes?: Record<string, unknown>,
): void {
  try {
    const event = getAccountCreateEvent(isAssumptionLoan, outcome);
    if (attributes) {
      trackRegistrationEvent(event, attributes);
    } else {
      trackRegistrationEvent(event);
    }
  } catch {
    // Analytics must not block flow
  }
}
